import React, { useState, useEffect } from 'react';
import { Box, Button, TextField, Typography, Link, Container, Paper, Alert, Divider, InputAdornment, IconButton } from '@mui/material';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import { Link as RouterLink, useNavigate } from 'react-router-dom';
import { Google as GoogleIcon, Visibility, VisibilityOff, Support } from '@mui/icons-material';
import MetaTags from '../../components/SEO/MetaTags';
// Use logo from public folder to avoid build path issues
import { useAuth } from '../../contexts/AuthContext';
import { useAnalytics } from '../../contexts/AnalyticsContext';
import API_CONFIG from '../../config/api';
import { getApiUrl } from '../../config/api';
import { getFirebaseErrorMessage, isTemporaryError } from '../../utils/firebaseErrors';
import { setClarityUser } from '../../utils/clarity'; // added import

// Use logo from public folder to avoid build path issues
const logo = process.env.PUBLIC_URL + '/logo.png';

const SignUp = () => {
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [referrerUrl, setReferrerUrl] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const { signup, loginWithGoogle, user } = useAuth();
  const { logPageView, logAuthEvent, logFormSubmission } = useAnalytics();
  const navigate = useNavigate();

  useEffect(() => {
    setReferrerUrl(document.referrer || '');
    console.log("referrerUrl set to:", document.referrer);
    const checkOnboardingStatus = async () => {
      if (user) {
        try {
          // Only run this check if user came from direct auth state change (not from button click)
          const checkResponse = await fetch(getApiUrl(API_CONFIG.ENDPOINTS.AUTH.CHECK_USER_ONBOARDING), {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json'
            },
            body: JSON.stringify({
              user_id: user.uid
            })
          });

          const checkData = await checkResponse.json();

          // If user has completed onboarding, go to dashboard
          if (checkData.success && checkData.hasCompletedOnboarding) {
            navigate('/dashboard');
          } else {
            // Otherwise, continue to onboarding
            navigate('/onboarding');
          }
        } catch (error) {
          console.error('Error checking onboarding status:', error);
          // Default to onboarding on error
          navigate('/onboarding');
        }
      }
    };

    if (user) {
      checkOnboardingStatus();
    }
  }, [user, navigate]);

  const handleFormSubmit = async (values) => {
    try {
      setError('');
      setLoading(true);

      // First create Firebase user
      const { error, user } = await signup(values.email, values.password);

      if (error) {
        const errorMessage = getFirebaseErrorMessage(error);
        setError(errorMessage);
        logAuthEvent('signup_error', {
          method: 'email',
          error: error.code || 'unknown',
          email_domain: values.email.split('@')[1]
        });
        return;
      }

      // Then save user data to MySQL
      try {
        const response = await fetch(getApiUrl(API_CONFIG.ENDPOINTS.AUTH.SAVE_USER), {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            id: user.uid,           // Using Firebase UID as id
            email: values.email,
            first_name: values.name.split(' ')[0],
            last_name: values.name.split(' ').slice(1).join(' '),
            display_name: values.name,
            referrer_url: referrerUrl // Pass the referrer URL
          })
        });

        const data = await response.json();

        if (!data.success) {
          throw new Error(data.error || 'Failed to save user data');
        }

        logAuthEvent('signup_success', {
          method: 'email',
          userId: user.uid,
          email_domain: values.email.split('@')[1]
        });

         try {
          setClarityUser(null, user.uid);
        } catch (e) {
          console.warn('Failed to set clarity user', e);
        }

        navigate('/onboarding');
      } catch (saveError) {
        console.error('Error saving user:', saveError);
        setError('Account created but failed to save additional data');
        logAuthEvent('signup_save_error', {
          method: 'email',
          error: saveError.message
        });
      }

    } catch (err) {
      setError('Failed to create an account');
      logAuthEvent('signup_error', {
        method: 'email',
        error: 'unknown_error'
      });
    } finally {
      setLoading(false);
    }
  };

  const formik = useFormik({
    initialValues: {
      name: '',
      email: '',
      password: '',
      confirmPassword: '',
    },
    validationSchema: Yup.object({
      name: Yup.string().required('Required'),
      email: Yup.string().email('Invalid email address').required('Required'),
      password: Yup.string()
        .min(8, 'Must be at least 8 characters')
        .matches(
          /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)/,
          'Must contain at least one uppercase letter, one lowercase letter, and one number'
        )
        .required('Required'),
      confirmPassword: Yup.string()
        .oneOf([Yup.ref('password'), null], 'Passwords must match')
        .required('Required'),
    }),
    onSubmit: handleFormSubmit
  });

  const handleGoogleSignIn = async () => {
    try {
      setError('');
      setLoading(true);
      const { error, user } = await loginWithGoogle();

      if (error) {
        const errorMessage = getFirebaseErrorMessage(error);
        setError(errorMessage);
        logAuthEvent('signup_error', { method: 'google', error: error.code || 'unknown' });
        return;
      }

      // First, check if the user has already completed onboarding
      try {
        const checkResponse = await fetch(getApiUrl(API_CONFIG.ENDPOINTS.AUTH.CHECK_USER_ONBOARDING), {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            user_id: user.uid
          })
        });

        const checkData = await checkResponse.json();

        // If user has already completed onboarding, redirect to dashboard
        if (checkData.success && checkData.hasCompletedOnboarding) {
          logAuthEvent('login_success', {
            method: 'google',
            userId: user.uid,
            email_domain: user.email.split('@')[1]
          });

            setClarityUser(null, user.uid);
          console.log('clarity user set for google login',  'responderId', user.uid);
          

          navigate('/dashboard');
          return;
        }

        // Otherwise, save/update user data and continue to onboarding
        const response = await fetch(getApiUrl(API_CONFIG.ENDPOINTS.AUTH.SAVE_USER), {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            id: user.uid,
            email: user.email,
            first_name: user.displayName?.split(' ')[0] || '',
            last_name: user.displayName?.split(' ').slice(1).join(' ') || '',
            display_name: user.displayName || ''
          })
        });

        const data = await response.json();

        if (!data.success) {
          throw new Error(data.error || 'Failed to save user data');
        }

        logAuthEvent('signup_success', {
          method: 'google',
          userId: user.uid,
          email_domain: user.email.split('@')[1]
        });

        // Only redirect to onboarding if the user hasn't completed it yet
        navigate('/onboarding');

      } catch (apiError) {
        console.error('Error checking user onboarding status:', apiError);
        setError('Authentication successful but failed to process user data');
        logAuthEvent('signup_save_error', {
          method: 'google',
          error: apiError.message
        });
      }
    } catch (err) {
      setError('Failed to sign in with Google');
      logAuthEvent('signup_error', {
        method: 'google',
        error: 'unknown_error'
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <MetaTags
        title="Sign Up"
        description="Join SurveySlack - Create powerful surveys with AI and intuitive drag & drop features"
        canonical="https://surveyslack.com/signup"
      />
      <Container component="main" maxWidth="sm">
        <Box
          sx={{
            marginTop: 8,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
          }}
        >
          <Paper
            elevation={0}
            sx={{
              p: 4,
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              width: '100%',
              border: '1px solid',
              borderColor: 'grey.200',
              borderRadius: 2,
            }}
          >
            <Box sx={{ mb: 2 }}>
              <img src={logo} alt="SurveySlack" style={{ height: '50px' }} />
            </Box>
            <Typography component="h1" variant="h4" sx={{ mb: 1, fontWeight: 700 }}>
              Create Account
            </Typography>
            <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
              Start creating amazing surveys with AI assistance
            </Typography>
            {error && (
              <Alert 
                severity="error" 
                sx={{ 
                  width: '100%', 
                  mb: 2,
                  position: 'relative',
                  '& .MuiAlert-message': {
                    width: '100%',
                    paddingBottom: '32px' // Space for the button
                  }
                }}
              >
                {error}
                <Button 
                  color="inherit"
                  size="small"
                  startIcon={<Support />}
                  href="mailto:support@surveyslack.com"
                  sx={{ 
                    position: 'absolute',
                    bottom: 8,
                    right: 8,
                    textTransform: 'none',
                    fontSize: '0.75rem',
                    minWidth: 'auto',
                    whiteSpace: 'nowrap',
                    '&:hover': {
                      backgroundColor: 'rgba(255, 255, 255, 0.1)'
                    }
                  }}
                >
                  Contact Support
                </Button>
              </Alert>
            )}
            <Box component="form" onSubmit={formik.handleSubmit} sx={{ width: '100%' }}>
              <TextField
                margin="normal"
                fullWidth
                id="name"
                name="name"
                label="Full Name"
                autoComplete="name"
                autoFocus
                value={formik.values.name}
                onChange={formik.handleChange}
                error={formik.touched.name && Boolean(formik.errors.name)}
                helperText={formik.touched.name && formik.errors.name}
                disabled={loading}
                InputLabelProps={{
                  shrink: true,
                }}
              />
              <TextField
                margin="normal"
                fullWidth
                id="email"
                name="email"
                label="Email Address"
                autoComplete="email"
                value={formik.values.email}
                onChange={formik.handleChange}
                error={formik.touched.email && Boolean(formik.errors.email)}
                helperText={formik.touched.email && formik.errors.email}
                disabled={loading}
                InputLabelProps={{
                  shrink: true,
                }}
              />
              <TextField
                margin="normal"
                fullWidth
                name="password"
                label="Password"
                type={showPassword ? 'text' : 'password'}
                id="password"
                autoComplete="new-password"
                value={formik.values.password}
                onChange={formik.handleChange}
                error={formik.touched.password && Boolean(formik.errors.password)}
                helperText={formik.touched.password && formik.errors.password}
                disabled={loading}
                InputLabelProps={{
                  shrink: true,
                }}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        aria-label="toggle password visibility"
                        onClick={() => setShowPassword(!showPassword)}
                        onMouseDown={(event) => event.preventDefault()}
                        edge="end"
                        disabled={loading}
                      >
                        {showPassword ? <VisibilityOff /> : <Visibility />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />
              <TextField
                margin="normal"
                fullWidth
                name="confirmPassword"
                label="Confirm Password"
                type={showConfirmPassword ? 'text' : 'password'}
                id="confirmPassword"
                autoComplete="new-password"
                value={formik.values.confirmPassword}
                onChange={formik.handleChange}
                error={formik.touched.confirmPassword && Boolean(formik.errors.confirmPassword)}
                helperText={formik.touched.confirmPassword && formik.errors.confirmPassword}
                disabled={loading}
                InputLabelProps={{
                  shrink: true,
                }}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        aria-label="toggle confirm password visibility"
                        onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                        onMouseDown={(event) => event.preventDefault()}
                        edge="end"
                        disabled={loading}
                      >
                        {showConfirmPassword ? <VisibilityOff /> : <Visibility />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />
              <Button
                type="submit"
                fullWidth
                variant="contained"
                sx={{ mt: 3, mb: 2, py: 1.5 }}
                disabled={loading}
              >
                Sign Up
              </Button>
              <Divider sx={{ my: 2 }}>or</Divider>
              <Button
                fullWidth
                variant="outlined"
                startIcon={<GoogleIcon />}
                onClick={handleGoogleSignIn}
                disabled={loading}
                sx={{ mb: 2, py: 1.5 }}
              >
                Sign up with Google
              </Button>
              <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
                <Link
                  component={RouterLink}
                  to="/login"
                  variant="body2"
                  sx={{ color: 'text.secondary', '&:hover': { color: 'primary.main' } }}
                >
                  Already have an account? Sign in
                </Link>
              </Box>
              <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3, pt: 2, borderTop: '1px solid', borderColor: 'grey.200' }}>
                <Typography variant="body2" color="text.secondary" sx={{ mr: 1 }}>
                  Need help?
                </Typography>
                <Link
                  href="mailto:support@surveyslack.com"
                  variant="body2"
                  sx={{ color: 'primary.main', textDecoration: 'none', '&:hover': { textDecoration: 'underline' } }}
                >
                  Contact Support
                </Link>
              </Box>
            </Box>
          </Paper>
        </Box>
      </Container>
    </>
  );
};

export default SignUp;
